import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { CheckCircle2, AlertTriangle, ClipboardCheck, ChevronRight, Loader2 } from "lucide-react";

const publicApi = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  headers: { "Content-Type": "application/json" },
});

// ────────────────────────── Field renderers ──────────────────────────
function BasicField({ q, value, onChange }) {
  const common = "w-full px-3 py-2 rounded-sm border border-input bg-card text-sm";
  if (q.type === "single") {
    return (
      <div className="flex flex-wrap gap-2" data-testid={`basic-${q.id}`}>
        {q.options.map((opt) => (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(opt)}
            className={`px-3 py-1.5 rounded-sm border text-sm transition-colors ${
              value === opt ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:bg-muted"
            }`}
            data-testid={`basic-${q.id}-opt-${opt}`}
          >
            {opt}
          </button>
        ))}
      </div>
    );
  }
  if (q.type === "date") {
    return (
      <input
        type="date"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className={`${common} max-w-xs`}
        data-testid={`basic-${q.id}`}
      />
    );
  }
  return (
    <input
      type={q.type === "tel" ? "tel" : "text"}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={q.placeholder || ""}
      className={common}
      data-testid={`basic-${q.id}`}
    />
  );
}

function YesNoField({ id, value, onChange }) {
  return (
    <div className="flex gap-2 flex-none" data-testid={`sym-${id}`}>
      {[
        { key: true, label: "Sim" },
        { key: false, label: "Não" },
      ].map((opt) => (
        <button
          key={String(opt.key)}
          type="button"
          onClick={() => onChange(opt.key)}
          className={`min-w-[64px] px-3 py-1.5 rounded-sm border text-sm font-medium transition-colors ${
            value === opt.key
              ? opt.key
                ? "bg-emerald-600 text-white border-emerald-600"
                : "bg-slate-700 text-white border-slate-700"
              : "bg-card border-border hover:bg-muted"
          }`}
          data-testid={`sym-${id}-${opt.key ? "sim" : "nao"}`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function BristolPicker({ image_url, types, value, onChange }) {
  return (
    <div data-testid="bristol-picker">
      <div className="rounded-sm border border-border overflow-hidden bg-card mb-4">
        <img
          src={image_url}
          alt="Escala de Bristol das fezes — 7 tipos"
          className="w-full h-auto"
          data-testid="bristol-image"
          loading="lazy"
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {types.map((t) => {
          const active = value === t.id;
          const bandClr =
            t.band === "constipacao"
              ? "border-amber-500 text-amber-800"
              : t.band === "regular"
              ? "border-emerald-600 text-emerald-800"
              : "border-orange-500 text-orange-800";
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => onChange(t.id)}
              className={`text-left rounded-sm border-2 p-3 transition-all ${
                active
                  ? "bg-primary text-primary-foreground border-primary shadow"
                  : `bg-card ${bandClr} hover:bg-muted/60`
              }`}
              data-testid={`bristol-opt-${t.id}`}
            >
              <div className="flex items-center gap-2 mb-0.5">
                <span className="text-xs uppercase tracking-wider font-bold">
                  {t.label}
                </span>
              </div>
              <div className={`text-sm ${active ? "" : "text-foreground"}`}>{t.description}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ────────────────────────── Main page ──────────────────────────
export default function PublicAnamnesisPage() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true });
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  // Confirmation gate — the Enviar button only appears after the patient has
  // explicitly reviewed the auto-summary of positive symptoms.
  const [reviewed, setReviewed] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await publicApi.get(`/public/anamnesis/${token}`);
        setState({ loading: false, ok: true, data });
        if (data.status === "submitted") {
          setSubmitted(true);
        } else if (data.status === "expired") {
          setState({ loading: false, ok: false, error: "expired" });
        }
        // Prefill patient_name if backend supplied one
        if (data.patient_name) {
          setAnswers((a) => ({ ...a, nome: data.patient_name }));
        }
      } catch (e) {
        setState({ loading: false, ok: false, error: e.response?.data?.detail || "not_found" });
      }
    })();
  }, [token]);

  const schema = state.data?.schema;
  const positive = useMemo(() => {
    if (!schema) return 0;
    return schema.symptom_questions.reduce((acc, q) => (answers[q.id] === true ? acc + 1 : acc), 0);
  }, [answers, schema]);
  const positiveLabels = useMemo(() => {
    if (!schema) return [];
    return schema.symptom_questions.filter((q) => answers[q.id] === true).map((q) => q.label);
  }, [answers, schema]);

  const validate = () => {
    if (!schema) return "Formulário não carregado.";
    for (const q of schema.basic_questions) {
      if (q.required) {
        const v = answers[q.id];
        if (!v || (typeof v === "string" && !v.trim())) {
          return `Preencha "${q.label}".`;
        }
      }
    }
    // Every symptom must be answered (Sim/Não).
    const missing = schema.symptom_questions.find((q) => typeof answers[q.id] !== "boolean");
    if (missing) return `Responda Sim/Não em "${missing.label}".`;
    if (!answers.bristol) return "Selecione um tipo na Escala de Bristol.";
    return null;
  };

  const submit = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setSubmitting(true);
    try {
      const { data } = await publicApi.post(`/public/anamnesis/${token}/submit`, { answers });
      setSubmitted(true);
      toast.success("Anamnese enviada com sucesso!");
      setState((s) => ({ ...s, submitResult: data }));
    } catch (e) {
      const msg = e.response?.data?.detail || "Erro ao enviar.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (state.loading) {
    return (
      <div className="min-h-screen bg-background grid place-items-center p-6" data-testid="anamnesis-loading">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!state.ok) {
    return (
      <div className="min-h-screen bg-background grid place-items-center p-6">
        <div className="max-w-lg text-center" data-testid="anamnesis-error">
          <AlertTriangle className="w-12 h-12 text-destructive mx-auto mb-3" />
          <h1 className="font-heading text-2xl font-bold mb-2">Link inválido ou expirado</h1>
          <p className="text-muted-foreground text-sm">
            {state.error === "expired"
              ? "Este link para preencher a Ficha de Anamnese expirou. Solicite um novo à equipe clínica."
              : "Não conseguimos encontrar essa ficha. Verifique o link e tente novamente."}
          </p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-background grid place-items-center p-6">
        <div className="max-w-lg text-center" data-testid="anamnesis-success">
          <CheckCircle2 className="w-14 h-14 text-emerald-600 mx-auto mb-3" />
          <h1 className="font-heading text-2xl font-bold mb-2">Ficha enviada</h1>
          <p className="text-muted-foreground text-sm">
            Obrigado! Sua Ficha de Anamnese foi registrada e será revisada antes da sua consulta.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="public-anamnesis-page">
      {/* Header */}
      <header className="bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto px-5 py-8">
          <div className="overline text-gold mb-2">Centro de Performance Farmacêutica</div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold">Ficha de Anamnese</h1>
          <p className="text-primary-foreground/80 text-sm mt-2 max-w-2xl">
            Responda com calma antes da sua 1ª consulta. Estas informações ajudam a personalizar o
            protocolo clínico e a identificar sinais precoces de carências nutricionais.
          </p>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 py-8 space-y-8">
        {/* Progress */}
        <div className="sticky top-0 -mx-5 px-5 py-2 bg-background/95 backdrop-blur z-10 border-b border-border flex items-center justify-between text-xs">
          <div className="overline text-muted-foreground">
            <ClipboardCheck className="w-3.5 h-3.5 inline mr-1" />
            {positive} de {schema.symptom_questions.length} sintomas respondidos "Sim"
          </div>
        </div>

        {/* Dados básicos */}
        <section className="bg-card border border-border rounded-sm p-6 space-y-4" data-testid="section-basic">
          <div>
            <div className="overline text-accent mb-1">Dados básicos</div>
            <h2 className="font-heading text-xl font-semibold text-primary">Sobre você</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {schema.basic_questions.map((q) => (
              <div key={q.id} className={q.id === "nome" ? "md:col-span-2" : ""}>
                <label className="text-sm font-medium mb-1 block">
                  {q.label} {q.required && <span className="text-destructive">*</span>}
                </label>
                <BasicField
                  q={q}
                  value={answers[q.id]}
                  onChange={(v) => setAnswers((a) => ({ ...a, [q.id]: v }))}
                />
              </div>
            ))}
          </div>
        </section>

        {/* Sintomas Sim/Não */}
        <section className="bg-card border border-border rounded-sm p-6" data-testid="section-symptoms">
          <div className="mb-4">
            <div className="overline text-accent mb-1">Sinais e sintomas</div>
            <h2 className="font-heading text-xl font-semibold text-primary">Nos últimos 30 dias você sentiu…</h2>
            <p className="text-xs text-muted-foreground mt-1">
              Responda Sim ou Não em cada linha. Nenhuma resposta é obrigatória ser positiva.
            </p>
          </div>
          <ol className="divide-y divide-border">
            {schema.symptom_questions.map((q, idx) => (
              <li
                key={q.id}
                className="py-3 flex items-start justify-between gap-3"
                data-testid={`symptom-row-${q.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-foreground flex gap-2">
                    <span className="text-muted-foreground w-6 text-right flex-none">
                      {idx + 1}.
                    </span>
                    <span>{q.label}</span>
                  </div>
                </div>
                <YesNoField
                  id={q.id}
                  value={answers[q.id]}
                  onChange={(v) => setAnswers((a) => ({ ...a, [q.id]: v }))}
                />
              </li>
            ))}
          </ol>
        </section>

        {/* Bristol */}
        <section className="bg-card border border-border rounded-sm p-6" data-testid="section-bristol">
          <div className="mb-4">
            <div className="overline text-accent mb-1">Escala de Bristol</div>
            <h2 className="font-heading text-xl font-semibold text-primary">
              Qual tipo mais se parece com suas fezes na maior parte da semana?
            </h2>
            <p className="text-xs text-muted-foreground mt-1">
              Escolha um único tipo (1 a 7) com base na imagem abaixo.
            </p>
          </div>
          <BristolPicker
            image_url={schema.bristol.image_url}
            types={schema.bristol.types}
            value={answers.bristol}
            onChange={(v) => setAnswers((a) => ({ ...a, bristol: v }))}
          />
        </section>

        {/* Auto-summary of positive symptoms — must be viewed before Enviar appears */}
        <section
          className="bg-primary/5 border border-primary/20 rounded-sm p-6"
          data-testid="anamnesis-summary"
        >
          <div className="overline text-accent mb-1">Antes de enviar</div>
          <h2 className="font-heading text-xl font-semibold text-primary mb-3">
            Você identificou <span className="text-emerald-700">{positive}</span> sintoma{positive === 1 ? "" : "s"} positivo{positive === 1 ? "" : "s"} hoje.
          </h2>
          <p className="text-sm text-muted-foreground mb-3">
            Confira sua lista antes de prosseguir:
          </p>
          {positiveLabels.length === 0 ? (
            <p className="text-sm text-muted-foreground italic" data-testid="summary-empty">
              Você marcou "Não" em todos os sintomas. Se estiver certo, prossiga; se quiser rever, role a página para cima.
            </p>
          ) : (
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1.5 list-disc pl-5 mb-2" data-testid="summary-list">
              {positiveLabels.map((label, i) => (
                <li key={i} className="text-sm text-foreground leading-snug">{label}</li>
              ))}
            </ul>
          )}
          <label className="mt-4 flex items-start gap-2 text-sm cursor-pointer select-none" data-testid="summary-confirm-label">
            <input
              type="checkbox"
              checked={reviewed}
              onChange={(e) => setReviewed(e.target.checked)}
              className="mt-1 accent-emerald-600"
              data-testid="summary-confirm-checkbox"
            />
            <span>
              Revisei a lista acima e confirmo que está correta.
            </span>
          </label>
        </section>

        {/* Submit — só aparece depois que o paciente marca a confirmação da revisão */}
        {reviewed && (
          <div className="flex justify-end" data-testid="submit-block">
            <button
              onClick={submit}
              disabled={submitting}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-sm bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-60"
              data-testid="submit-anamnesis-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronRight className="w-4 h-4" />}
              {submitting ? "Enviando…" : "Enviar Ficha de Anamnese"}
            </button>
          </div>
        )}
      </main>
    </div>
  );
}
